import { recordedBy } from "./attribution";
import {
  FINDING_OUTCOMES,
  type FindingRow,
  type SessionState,
  type TranscriptMessage,
} from "../api/client";

/**
 * The session transcript, as the markdown file behind *Export chat* on the lead auditor's
 * and the client's consoles.
 *
 * **It is a copy of the screen, not a new disclosure.** Every line is built from the
 * `SessionState` the caller's own console is already rendering, so a viewer's export holds
 * exactly what their projection holds and nothing the server did not already send them.
 * That is why this is composed in the browser rather than added to `exports.py`: the
 * artefacts there are the signed plan and report, gated by `reporting.exportMatrix`, and a
 * transcript copy has no gate to resolve because the reader has already read it. ⚠ The
 * corollary is that this leaves no trail event — it is Ctrl-P, not a release — so it is
 * offered to the lead auditor and the client only, never to a certification body observer,
 * for whom taking a copy of a draft holding an undisposed proposal is the ISO/IEC 17021-1
 * Cl. 5.2 exposure `exports.py` exists to refuse.
 *
 * **Preliminary stays preliminary.** ADR 0024's three protections survive the export or the
 * file silently promotes a proposal to a determination: the label is on the finding block,
 * the pending count stays beside the determined one rather than merged into it, and a
 * disposition is written where it happened in the thread rather than being folded back into
 * the finding it decided. There is deliberately **no findings summary at the end** — a table
 * restated after the fact is where a withdrawn grade goes missing.
 *
 * **Complao's words carry markdown; a person's carry their own characters.** The four things
 * `RichText` renders are the four things the prompt asks for, so a Complao body is already
 * markdown and passes through as written. What the client and the lead auditor typed is
 * emitted verbatim and is neither escaped nor reformatted — an asterisk a witness typed is
 * part of the testimony, and quoting it into something safer changes how the record reads.
 */
export function transcriptMarkdown(state: SessionState, exportedAt: Date): string {
  const out: string[] = [];

  out.push(`# Stage 1 audit — ${state.client_organisation}`, "");
  out.push(`- **Reference:** ${state.reference}`);
  out.push(`- **Standards:** ${state.standards.join(" · ")}`);
  out.push(`- **Certification body:** ${state.certification_body}`);
  out.push(`- **Lead auditor:** ${state.lead_auditor ?? "—"}`);
  out.push(`- **Session status:** ${status(state)}`);
  out.push(`- **Audit time:** ${state.elapsed_hours} of ${state.session_hours} hours`);
  out.push(`- **Findings:** ${tally(state)}`);
  out.push(`- **Exported:** ${stamp(exportedAt)}`);
  out.push("");

  const participants = state.participants ?? [];
  if (participants.length) {
    out.push("## Attendance", "");
    for (const person of participants) {
      out.push(`- ${person.name} — ${person.role} (joined ${clock(person.joined_at)})`);
    }
    out.push("");
  }

  out.push("## Transcript", "");
  for (const message of state.transcript ?? []) {
    out.push(...entry(message, state.findings ?? []));
    out.push("");
  }

  return `${out.join("\n").trimEnd()}\n`;
}

/** Compose the file and hand it to the browser. */
export function downloadTranscript(state: SessionState, exportedAt: Date): void {
  const blob = new Blob([transcriptMarkdown(state, exportedAt)], {
    type: "text/markdown;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename(state, exportedAt);
  anchor.click();
  // The bytes are taken by the time the click returns; holding the blob keeps a whole
  // session's transcript in memory for as long as the console stays open.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function filename(state: SessionState, exportedAt: Date): string {
  const reference = state.reference.replace(/[^A-Za-z0-9._-]+/g, "-").replace(/^-|-$/g, "");
  const day = `${exportedAt.getFullYear()}-${two(exportedAt.getMonth() + 1)}-${two(exportedAt.getDate())}`;
  return `stage1-transcript-${reference || "audit"}-${day}.md`;
}

/**
 * One message, in the shape its bubble has on screen.
 *
 * The kinds are the console's own and are handled in the same order, because a reader
 * comparing the file against the screen has to find the same things in the same places.
 */
function entry(message: TranscriptMessage, findings: FindingRow[]): string[] {
  const at = clock(message.created_at);
  const clause = message.clause_ref ? ` — Cl. ${message.clause_ref}` : "";

  if (message.author === "system") {
    return [`*${at} · ${message.body ?? ""}*`];
  }

  if (message.kind === "finding") {
    const finding = findings.find(
      (row) => row.id === (message.payload?.finding_id as string | undefined),
    );
    const payload = message.payload ?? {};
    // The same reading the card makes, and from the same place: the payload is what was
    // true when the client read the message, so a later disposition does not rewrite the
    // label on a message they have already seen.
    const preliminary = Boolean(payload.preliminary) && finding?.disposition == null;
    const proposed = payload.origin === "ai_proposed";
    const who = proposed
      ? preliminary
        ? "Complao — preliminary classification"
        : "Complao — proposal, decided by the lead auditor"
      : "Lead auditor — finding recorded";
    const outcome = String(finding?.outcome ?? payload.outcome ?? "");

    const lines = [`**${at} · ${who}**${clause}`, ""];
    lines.push(`> **${preliminary ? "Preliminary classification" : "Finding recorded"}**`, ">");
    lines.push(`> - Clause: ${finding?.clause_ref ?? String(payload.clause_ref ?? "—")}`);
    lines.push(`> - Section: Clause ${finding?.section ?? "—"}`);
    lines.push(`> - Classification: ${label(outcome)}`);
    lines.push(`> - Evidence presented: ${finding?.statement ?? String(payload.statement ?? "")}`);
    // Absent, not omitted — the client's projection withholds the basis, so their export
    // has no sentence to print and an empty bullet would read as a finding graded on
    // nothing. The lead auditor's and the certification body's exports still carry it.
    const basis = finding?.basis ?? (payload.basis == null ? null : String(payload.basis));
    if (basis) lines.push(`> - Basis for classification: ${basis}`);
    lines.push(`> - Recorded by: ${recordedBy(finding, payload, message.author_name)} · ${at}`);
    if (preliminary) {
      lines.push(
        ">",
        "> Subject to the lead auditor's determination. Audit findings are determined by",
        "> the audit team — ISO/IEC 17021-1 Cl. 9.4.5.",
      );
    }
    return lines;
  }

  if (message.kind === "document") {
    const payload = message.payload ?? {};
    const filename = String(payload.filename ?? "document");
    const who = `Client sent a document — ${message.author_name ?? "the client"}`;
    return [`**${at} · ${who}**${clause}`, "", `> Document: ${filename}`];
  }

  if (message.kind === "disposition") {
    return [`**${at} · Lead auditor — determination**${clause}`, "", `> ${message.body ?? ""}`];
  }

  if (message.kind === "section_summary") {
    const payload = message.payload ?? {};
    const rows = (payload.rows ?? []) as {
      clause_ref: string;
      title: string;
      outcome: string | null;
      preliminary: boolean;
      withdrawn?: boolean;
    }[];
    const lines = [
      `**${at} · Section ${String(payload.section ?? "")} — summary of findings**`,
      "",
      "| Clause | Requirement | Classification |",
      "| --- | --- | --- |",
    ];
    for (const row of rows) {
      const grade =
        row.outcome === null || row.withdrawn
          ? row.withdrawn
            ? "Withdrawn — no finding"
            : "No finding recorded"
          : `${label(row.outcome)}${row.preliminary ? " — preliminary" : ""}`;
      lines.push(`| ${cell(row.clause_ref)} | ${cell(row.title)} | ${cell(grade)} |`);
    }
    return lines;
  }

  if (message.author === "ai") {
    const item = message.clause_ref
      ? clause
      : message.payload?.item
        ? ` — ${String(message.payload.item)}`
        : "";
    return [`**${at} · Complao audit team member**${item}`, "", message.body ?? ""];
  }

  const who =
    message.author === "auditee"
      ? `Client response — ${message.author_name ?? "the client"}`
      : `Lead auditor ${message.payload?.intervention ?? "note"}`;
  const flag = message.flags?.inadequate ? " ⚠ Insufficient" : "";
  return [`**${at} · ${who}**${clause}${flag}`, "", message.body ?? ""];
}

/**
 * The header tally, read from the server's own counts.
 *
 * All four grades, always, and pending kept in its own brackets — the two rules the badge
 * carries. Nothing is recounted here: a determination is persisted, never recomputed.
 */
function tally(state: SessionState): string {
  return FINDING_OUTCOMES.map((outcome) => {
    const total = (state.tally ?? {})[outcome.key] ?? 0;
    const pending = (state.pending ?? {})[outcome.key] ?? 0;
    return `${outcome.short} ${total}${pending ? ` (${pending} pending)` : ""}`;
  }).join(" · ");
}

function label(outcome: string): string {
  return FINDING_OUTCOMES.find((entry) => entry.key === outcome)?.label ?? (outcome || "—");
}

function status(state: SessionState): string {
  if (state.status === "paused") {
    return `paused — ${state.pause_reason ?? "no reason recorded"}`;
  }
  if (state.status === "closed") return "closed";
  return state.status ?? "not yet opened";
}

/** A pipe inside a cell ends the cell, and a summary row's title is free text. */
function cell(value: string): string {
  return value.replace(/\|/g, "\\|").replace(/\n/g, " ");
}

function clock(value: string): string {
  return new Date(value).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function stamp(value: Date): string {
  return `${value.getFullYear()}-${two(value.getMonth() + 1)}-${two(value.getDate())} ${value.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

function two(value: number): string {
  return String(value).padStart(2, "0");
}
